import React from 'react';
import { LocalServicePage } from '../../components/LocalServicePage';

export const LocksmithHavering = () => {
  return (
    <LocalServicePage
      areaName="Havering"
      areaType="AdministrativeArea"
      slug="locksmith-havering"
      heroTitle="Door, Window & Lock Services Across Havering"
      heroSubtitle="Royal Security provides specialist door, window, and lock repairs throughout the London Borough of Havering. Based locally, we offer expert diagnosis, honest advice, and quality repairs for residential and rental properties."
      introContent={
        <>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Havering is home to diverse housing stock—from the characterful properties of Gidea Park to the family homes across Hornchurch, Upminster, and Harold Wood. Each area has its own typical window and door systems, and over years of working throughout the borough, we've developed deep expertise in all of them.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            As a locally-based specialist, we understand that door and window problems are more than just inconveniences—they affect your security, comfort, and energy bills. That's why we take a thorough approach: proper diagnosis first, then clear explanation of your options, and finally a repair that addresses the root cause rather than just the symptoms.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We work with homeowners across Havering, as well as landlords and letting agents managing rental properties in the borough. Our repeat customers value our honest assessments, fair pricing, and the fact that our repairs last.
          </p>
        </>
      }
      servicesEmphasis={[
        {
          title: "Multipoint Lock Systems",
          description: "Modern doors rely on multipoint locking mechanisms for security. When these fail—whether through wear, damage, or misalignment—we diagnose the specific fault and repair or replace the necessary components. We source quality replacement parts and can upgrade older systems if needed."
        },
        {
          title: "Composite & uPVC Door Repairs",
          description: "Composite and uPVC doors are standard across Havering. We handle all aspects: lock mechanisms, handles, hinges, letterboxes, panels, and draught seals. If your door is sticking, rattling, or not locking properly, we'll identify and fix the cause."
        },
        {
          title: "Window Mechanism Repairs",
          description: "From stuck casement windows to tilt and turn systems that won't function correctly, we repair window hardware and mechanisms. Friction stays, espagnolette locks, tilt mechanisms, restrictors—we carry common parts and source specialist items when needed."
        },
        {
          title: "Aluminium Door & Window Services",
          description: "Many properties in Havering have aluminium windows, bi-fold doors, or sliding systems. These require specific expertise and parts different from uPVC. We work on all major aluminium systems, including commercial and high-specification residential installations."
        },
        {
          title: "Sealed Unit Replacement",
          description: "When double glazing fails—misting, condensation between panes, visible seal damage—we replace the glass unit within your existing frame. This is far more economical than replacing entire windows and maintains the appearance of your property."
        },
        {
          title: "Emergency & Urgent Repairs",
          description: "Security issues and doors that won't lock need prompt attention. We prioritise genuine emergencies and give you a realistic arrival time when you call. For non-urgent matters, we'll book a convenient appointment and give you a clear timeframe."
        }
      ]}
      localContextTitle="Repair, Upgrade or Replace? Practical Advice for Havering Homes"
      localContext={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Repair:</strong> Stiff locks, dropped doors, broken handles and failed hinges can often be repaired. If inspection shows the frame is sound and square, we'll recommend repairing the component that has failed.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Upgrade:</strong> If your door uses an older euro cylinder, upgrading to an anti-snap cylinder rated TS007 3-star or SS312 Diamond is a relatively simple way to improve resistance to lock snapping. Handles, letterbox guards, and window restrictors are other simple upgrades worth considering.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            <strong>Replace:</strong> Whether replacement makes more sense depends on what we find on inspection: the condition of the frame and hardware, whether suitable parts are available, whether the door or window still suits its use, and how the repair cost compares. We'll show you what we've found and explain the options, so the decision is yours.
          </p>
        </>
      }
      commonProblems={[
        {
          title: "Multipoint lock not engaging fully",
          description: "The gearbox or hooks may be worn. We diagnose whether adjustment, part replacement, or full mechanism change is needed."
        },
        {
          title: "Composite door hard to open/close",
          description: "Often caused by expansion, settling, or hinge wear. We adjust alignment and replace components as needed."
        },
        {
          title: "Draughty windows despite being 'sealed'",
          description: "Gaskets and seals degrade over time. Replacement seals and adjustment often restore performance."
        },
        {
          title: "Bi-fold door not tracking smoothly",
          description: "Rollers, tracks, and alignment all affect operation. We service and repair bi-fold systems of all types."
        },
        {
          title: "Window restricted and won't open fully",
          description: "Faulty restrictors or mechanism issues. We repair or replace to restore full functionality."
        },
        {
          title: "Condensation between double glazing",
          description: "Seal failure between panes. We replace the sealed unit while keeping your existing frames."
        }
      ]}
      whyChooseUs={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            We've built our reputation in Havering by doing quality work at fair prices. Unlike companies that quote for replacements by default, we always consider repair options first. If your door mechanism can be repaired, we won't quote you for a new one.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our diagnostic approach means we identify the actual problem before starting work. We've seen too many cases where customers have paid for unnecessary parts or even replacement doors when a simple adjustment would have solved the issue. Proper diagnosis saves you money and ensures the repair actually works.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We're fully insured and guarantee our work. If there's ever an issue after a repair, we come back and sort it out. That confidence in our workmanship is built on experience and using quality parts—not the cheapest components that will fail again in six months.
          </p>
        </>
      }
      ctaText="Looking for Door & Window Specialists in Havering?"
    />
  );
};
